// src/components/Documents/DocumentViewer.js (Updated with Chrome-safe viewer)
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Download, 
  Trash2, 
  ArrowLeft, 
  Share, 
  FileText,
  ExternalLink,
  AlertCircle
} from 'lucide-react';
import toast from 'react-hot-toast';
import { documentService } from '../../services/documentService';
import { useAuth } from '../../contexts/AuthContext';
import SimplePDFViewer from './SimplePDFViewer';
import { analyticsService } from '../../services/analyticsService';

const DocumentViewer = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [document, setDocument] = useState(null);
  const [loading, setLoading] = useState(true);
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    loadDocument();
  }, [id, user]);

  const loadDocument = async () => {
    try {
      setLoading(true);
      const doc = await documentService.getDocument(id, user.uid);
      setDocument(doc);
      
      // Track document view
      if (doc) {
        analyticsService.trackDocumentView(
          doc.id, 
          user.uid, 
          doc.name
        ).catch(error => {
          console.error('Error tracking document view:', error);
        });
      }
    } catch (error) {
      toast.error('Error loading document');
      console.error(error);
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const downloadDocument = () => {
    if (document?.url) {
      // Track download asynchronously (non-blocking)
      analyticsService.trackDocumentDownload(
        document.id,
        user.uid,
        document.name
      ).catch(error => {
        console.error('Error tracking download:', error);
      });
      
      // Start download immediately
      const link = document.createElement('a');
      link.href = document.url;
      link.download = document.name;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const openInNewTab = () => {
    if (document?.url) {
      // Track view in new tab
      analyticsService.trackDocumentView(
        document.id,
        user.uid,
        document.name
      ).catch(error => {
        console.error('Error tracking view:', error);
      });
      
      window.open(document.url, '_blank', 'noopener,noreferrer');
    }
  };

  const deleteDocument = async () => {
    if (window.confirm('Are you sure you want to delete this document?')) {
      try {
        await documentService.deleteDocument(document.id, document.path, user.uid);
        toast.success('Document deleted successfully');
        navigate('/');
      } catch (error) {
        toast.error('Error deleting document');
        console.error(error);
      }
    }
  };

  const shareDocument = async () => {
    if (navigator.share && document?.url) {
      try {
        await navigator.share({
          title: document.name,
          url: document.url
        });
      } catch (error) {
        navigator.clipboard.writeText(document.url);
        toast.success('Document link copied to clipboard');
      }
    } else if (document?.url) {
      navigator.clipboard.writeText(document.url);
      toast.success('Document link copied to clipboard');
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Unknown';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileTypeCategory = (type) => {
    if (type === 'application/pdf') return 'pdf';
    if (type.startsWith('image/')) return 'image';
    if (type.includes('word') || type.includes('document')) return 'document';
    if (type.includes('sheet') || type.includes('excel')) return 'spreadsheet';
    if (type.includes('presentation') || type.includes('powerpoint')) return 'presentation';
    if (type === 'text/plain') return 'text';
    return 'other';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading document...</p>
        </div>
      </div>
    );
  }

  if (!document) {
    return (
      <div className="text-center py-12">
        <FileText className="w-12 h-12 mx-auto text-gray-400 mb-4" />
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Document not found</h2>
        <p className="text-gray-600 mb-4">The document you're looking for doesn't exist or has been removed.</p>
        <button
          onClick={() => navigate('/')}
          className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors"
        >
          Back to Dashboard
        </button>
      </div>
    );
  }

  const fileCategory = getFileTypeCategory(document.type);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/')}
            className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{document.name}</h1>
            <p className="text-sm text-gray-600">
              {formatFileSize(document.size)} • Modified {formatDate(document.updatedAt)}
            </p>
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          <button
            onClick={shareDocument}
            className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            title="Share"
          >
            <Share className="w-5 h-5" />
          </button>
          <button
            onClick={openInNewTab}
            className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            title="Open in new tab"
          >
            <ExternalLink className="w-5 h-5" />
          </button>
          <button
            onClick={downloadDocument}
            className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            title="Download"
          >
            <Download className="w-5 h-5" />
          </button>
          <button
            onClick={deleteDocument}
            className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
            title="Delete"
          >
            <Trash2 className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Document Content */}
      {fileCategory === 'pdf' ? (
        /* PDF Viewer */
        <SimplePDFViewer document={document} />
      ) : fileCategory === 'image' ? (
        /* Image Viewer */
        <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
          <div className="bg-gray-50 border-b px-4 py-3 flex justify-between items-center">
            <h3 className="text-sm font-medium text-gray-700">Image Preview</h3>
            <div className="flex space-x-2">
              <button
                onClick={openInNewTab}
                className="text-sm text-indigo-600 hover:text-indigo-800 flex items-center"
              >
                <ExternalLink className="w-4 h-4 mr-1" />
                Full Size
              </button>
              <button
                onClick={downloadDocument}
                className="text-sm text-indigo-600 hover:text-indigo-800 flex items-center"
              >
                <Download className="w-4 h-4 mr-1" />
                Download
              </button>
            </div>
          </div>
          <div className="p-4 bg-gray-100 min-h-96 flex justify-center items-center">
            {!imageError ? (
              <img
                src={document.url}
                alt={document.name}
                className="max-w-full max-h-96 object-contain shadow-lg rounded"
                onError={() => setImageError(true)}
                crossOrigin="anonymous"
              />
            ) : (
              <div className="text-center py-12">
                <AlertCircle className="w-12 h-12 mx-auto text-red-500 mb-4" />
                <p className="text-red-600 mb-4">Unable to display image</p>
                <div className="space-y-2">
                  <button
                    onClick={downloadDocument}
                    className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors flex items-center mx-auto"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download Image
                  </button>
                  <button
                    onClick={openInNewTab}
                    className="border border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors flex items-center mx-auto"
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Open in New Tab
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      ) : fileCategory === 'text' ? (
        /* Text File Viewer */
        <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
          <div className="bg-gray-50 border-b px-4 py-3">
            <h3 className="text-sm font-medium text-gray-700">Text File Content</h3>
          </div>
          <div className="p-4 bg-gray-100">
            <div className="bg-white rounded border p-4 max-h-96 overflow-auto">
              <iframe
                src={document.url}
                title={document.name}
                className="w-full h-80 border-0"
                sandbox="allow-same-origin"
              />
            </div>
          </div>
        </div>
      ) : (
        /* Other file types */
        <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
          <div className="p-8 text-center">
            <FileText className="w-16 h-16 mx-auto text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Preview not available</h3>
            <p className="text-gray-600 mb-4">
              This file type cannot be previewed in the browser. Download the file to view it.
            </p>
            <div className="space-y-3">
              <button
                onClick={downloadDocument}
                className="bg-indigo-600 text-white px-6 py-3 rounded-lg hover:bg-indigo-700 transition-colors flex items-center mx-auto"
              >
                <Download className="w-5 h-5 mr-2" />
                Download File
              </button>
              <button
                onClick={openInNewTab}
                className="border border-gray-300 text-gray-700 px-6 py-3 rounded-lg hover:bg-gray-50 transition-colors flex items-center mx-auto"
              >
                <ExternalLink className="w-5 h-5 mr-2" />
                Open in New Tab
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Document Info */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Document Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">File Name</label>
            <p className="mt-1 text-sm text-gray-900">{document.name}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">File Size</label>
            <p className="mt-1 text-sm text-gray-900">{formatFileSize(document.size)}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">File Type</label>
            <p className="mt-1 text-sm text-gray-900">{document.type}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Created</label>
            <p className="mt-1 text-sm text-gray-900">{formatDate(document.createdAt)}</p>
          </div>
          {document.description && (
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700">Description</label>
              <p className="mt-1 text-sm text-gray-900">{document.description}</p>
            </div>
          )}
          </div>
      </div>
    </div>
  );
};

export default DocumentViewer;